import {
  inject,
  onBeforeUnmount,
  provide,
  type InjectionKey,
  type Ref,
} from "vue";
import { classifyGesture } from "@/utils/gesture-direction";
import { getCanvasIconScale } from "./useCanvasUiScale";

/** Fenêtre (ms) des derniers mouvements qui donnent la vitesse au lâcher. */
const VELOCITY_WINDOW_MS = 100;
/** Doigt immobile depuis plus longtemps que ça au lâcher : pas d'élan. */
const MAX_IDLE_BEFORE_RELEASE_MS = 50;
/** Freinage de l'élan, par tranche de 16 ms (≈ une image à 60 Hz). */
const FRICTION_PER_FRAME = 0.95;
/** Vitesse (px/ms) sous laquelle l'élan s'arrête. */
const MIN_VELOCITY = 0.02;

interface Sample {
  t: number;
  x: number;
}

interface HorizontalSwipeContext {
  /**
   * Le geste en cours a commencé par stopper un élan : c'était pour arrêter
   * la ligne, pas pour toucher la note sous le doigt.
   */
  stoppedFling: () => boolean;
}

const HORIZONTAL_SWIPE_KEY: InjectionKey<HorizontalSwipeContext> =
  Symbol("horizontal-swipe");

/** Pour les éléments de la rangée (cf. NoteConfigButton). */
export function useHorizontalSwipeContext(): HorizontalSwipeContext | null {
  return inject(HORIZONTAL_SWIPE_KEY, null);
}

/**
 * Défilement horizontal au doigt (ou au stylet) d'une rangée dont les éléments
 * se glissent aussi vers le plan — la ligne des notes. Les éléments gardent
 * `touch-action: none` (la bibliothèque de glisser-déposer en dépend), le
 * navigateur ne fait donc aucun défilement natif : on le fait ici.
 *
 * Le geste est tranché comme dans le bouton glissable (cf. classifyGesture) :
 * horizontal → la rangée défile (avec élan au lâcher), vertical → on laisse
 * le bouton démarrer son glisser. La souris n'est pas concernée : elle garde
 * les flèches et la molette.
 *
 * `onPointerDown` est à poser en phase de capture sur la rangée (il doit
 * passer avant les boutons), les autres gestionnaires en phase normale : les
 * événements des boutons y remontent.
 */
export function useHorizontalSwipe(scroller: Ref<HTMLElement | null>) {
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let swiping = false;
  let abandoned = false;
  let samples: Sample[] = [];
  let inertiaFrame = 0;
  let stoppedFling = false;

  provide(HORIZONTAL_SWIPE_KEY, { stoppedFling: () => stoppedFling });

  const isTouchLike = (e: PointerEvent) =>
    e.pointerType === "touch" || e.pointerType === "pen";

  /** Arrête l'élan en cours. Renvoie vrai s'il y en avait un. */
  function stopInertia(): boolean {
    const wasRunning = inertiaFrame !== 0;
    if (inertiaFrame) cancelAnimationFrame(inertiaFrame);
    inertiaFrame = 0;
    return wasRunning;
  }

  function reset() {
    pointerId = null;
    swiping = false;
    abandoned = false;
    samples = [];
    // Les boutons ont déjà lu la valeur au pointerdown : elle ne vaut que pour
    // ce geste (sinon un clic souris suivant serait ignoré).
    stoppedFling = false;
  }

  function onPointerDown(e: PointerEvent) {
    if (!isTouchLike(e) || !scroller.value) return;
    // Un seul doigt suivi à la fois — le premier posé sur la rangée, même si
    // un autre doigt est déjà sur l'écran (main qui tient la tablette).
    if (pointerId !== null) return;
    stoppedFling = stopInertia();
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    startScrollLeft = scroller.value.scrollLeft;
    samples = [{ t: e.timeStamp, x: e.clientX }];
  }

  function onPointerMove(e: PointerEvent) {
    if (e.pointerId !== pointerId || abandoned || !scroller.value) return;
    const dx = e.clientX - startX;
    if (!swiping) {
      const direction = classifyGesture(dx, e.clientY - startY);
      if (direction === "pending") return;
      if (direction === "vertical") {
        // Le bouton attrape la note : ce geste n'est plus un défilement.
        abandoned = true;
        return;
      }
      swiping = true;
    }
    // Pointeur en pixels écran, défilement en pixels de la rangée (zoomée).
    scroller.value.scrollLeft = startScrollLeft - dx / getCanvasIconScale();
    samples.push({ t: e.timeStamp, x: e.clientX });
    const cutoff = e.timeStamp - VELOCITY_WINDOW_MS;
    while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
  }

  function onPointerUp(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    if (swiping) startInertia(e.timeStamp);
    reset();
  }

  function onPointerCancel(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    reset();
  }

  function startInertia(releaseTime: number) {
    // Seuls les mouvements juste avant le lâcher comptent : un doigt qui s'est
    // arrêté puis levé ne lance rien (aucun pointermove pendant l'arrêt).
    const recent = samples.filter(
      (sample) => sample.t >= releaseTime - VELOCITY_WINDOW_MS,
    );
    const first = recent[0];
    const last = recent[recent.length - 1];
    if (
      !first ||
      !last ||
      last.t <= first.t ||
      releaseTime - last.t > MAX_IDLE_BEFORE_RELEASE_MS
    ) {
      return;
    }
    let velocity = (last.x - first.x) / (last.t - first.t); // px/ms, écran
    let previous = performance.now();

    const step = (now: number) => {
      const el = scroller.value;
      if (!el) {
        inertiaFrame = 0;
        return;
      }
      const dt = Math.max(1, now - previous);
      previous = now;
      const before = el.scrollLeft;
      el.scrollLeft = before - (velocity * dt) / getCanvasIconScale();
      velocity *= Math.pow(FRICTION_PER_FRAME, dt / 16);
      // Arrêt : trop lent, ou bord atteint (le défilement ne bouge plus).
      if (Math.abs(velocity) < MIN_VELOCITY || el.scrollLeft === before) {
        inertiaFrame = 0;
        return;
      }
      inertiaFrame = requestAnimationFrame(step);
    };
    inertiaFrame = requestAnimationFrame(step);
  }

  onBeforeUnmount(stopInertia);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    stopInertia,
  };
}
